import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { users } from '../../../../interfaces/users';
import { EmployeesService } from '../../../../services/employees';
import { FinancialManagerService } from '../../../../services/financial-manager';
import Swal from 'sweetalert2';
import { UserService } from '../../../../services/user';

@Component({
  selector: 'app-employeemobile',
  templateUrl: './employeemobile.component.html',
  styleUrl: './employeemobile.component.scss'
})
export class EmployeemobileComponent implements OnInit {
  userData!: users;
  data!: any;

  id!: any;
  name!: any;
  job_title!: any;
  salary!: any;

  contentReady: boolean = true;

  uniqueDates: string[] = [];
  uniqueMonths: string[] = [];

  filteredDate: string = '';
  filteredMonth: string = '';

  allRecords: any[] = [];

  bonuses: any[] = [];
  loans: any[] = [];
  deductions: any[] = [];

  visibleBonuses: any[] = [];
  visibleLoans: any[] = [];
  visibleDeductions: any[] = [];

  originalBonuses: any[] = [];
  originalLoans: any[] = [];
  originalDeductions: any[] = [];

  showFinPopUp: boolean = false;

  amount: any = '';
  note: any = '';
  finId!: any;

  currentMonth!: string;
  salaryMonth!: string;
  userId!: any;

  isAr: boolean = localStorage.getItem('language') === 'ar';

  private loanLimit: number = 6;
  private bonusLimit: number = 6;
  private deductionLimit: number = 6;
  private readonly loadStep: number = 6;

  isLoadingMore: boolean = false;
  hasMoreData: boolean = false;

  constructor(
    private _userService: UserService,
    private _FinancialManager: FinancialManagerService,
    private _employessService: EmployeesService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    const now = new Date();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const year = now.getFullYear();

    this.currentMonth = `${year}-${month}`;
    this.salaryMonth = this.currentMonth;

    this._userService.getUserId().subscribe({
      next: (data) => {
        this.userId = data['user_id'];
      },
      error: (error) => console.error(error),
    });

    this.route.queryParams.subscribe(params => {
      this.data = params;

      this.id = params['id'] ?? history.state?.id;
      this.name = params['name'] ?? history.state?.name;
      this.job_title = params['job_title'] ?? history.state?.job_title;
      this.salary = params['salary'] ?? history.state?.salary;

      if (this.id) {
        this.getOneEmployee();
      } else {
        this.contentReady = false;
        Swal.fire({
          icon: 'error',
          title: this.isAr ? 'خطأ' : 'Error',
          text: this.isAr ? 'بيانات الموظف غير موجودة' : 'Employee data is missing',
        });
      }
    });
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    const scrollPosition =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;

    const windowHeight = window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;
    const bottomReached = scrollPosition + windowHeight >= pageHeight - 120;

    if (bottomReached) {
      this.loadMore();
    }
  }

  getOneEmployee(): void {
    this.contentReady = true;

    const today = new Date();
    const todayString = today.toISOString().substring(0, 10);
    const currentMonth = today.toISOString().substring(0, 7);

    this._employessService.getOneEmployees(this.id).subscribe({
      next: (data) => {
        this.contentReady = false;

        this.originalBonuses = data['bonuses'] || [];
        this.originalLoans = data['loans'] || [];
        this.originalDeductions = data['deductions'] || [];

        this.allRecords = [
          ...this.originalBonuses,
          ...this.originalLoans,
          ...this.originalDeductions
        ];

        const dateSet = new Set(
          this.allRecords
            .filter(rec => rec?.created_at)
            .map(rec => rec.created_at.substring(0, 10))
        );

        this.uniqueDates = Array.from(dateSet)
          .sort((a, b) => b.localeCompare(a));

        const monthSet = new Set(
          this.allRecords
            .filter(rec => rec?.created_at)
            .map(rec => rec.created_at.substring(0, 7))
        );

        this.uniqueMonths = Array.from(monthSet)
          .sort((a, b) => b.localeCompare(a));

        if (this.uniqueMonths.includes(this.currentMonth)) {
          this.salaryMonth = this.currentMonth;
        } else {
          this.salaryMonth = this.uniqueMonths[0] ?? this.currentMonth;
        }

        this.filteredDate = '';
        this.filteredMonth = this.uniqueMonths.includes(currentMonth)
          ? currentMonth
          : this.uniqueMonths[0] ?? '';

        if (this.filteredMonth) {
          this.filterByMonth();
        } else if (this.uniqueDates.includes(todayString)) {
          this.filteredDate = todayString;
          this.filterByDate();
        } else {
          this.loans = [...this.originalLoans];
          this.bonuses = [...this.originalBonuses];
          this.deductions = [...this.originalDeductions];
          this.resetVisibleItems();
        }
      },
      error: (error) => {
        this.contentReady = false;
        console.error(error);
      }
    });
  }

  filterByDate(): void {
    this.filteredMonth = '';

    if (!this.filteredDate) {
      this.clearFilters();
      return;
    }

    this.loans = this.originalLoans.filter((l) =>
      l.created_at?.startsWith(this.filteredDate)
    );

    this.bonuses = this.originalBonuses.filter((b) =>
      b.created_at?.startsWith(this.filteredDate)
    );

    this.deductions = this.originalDeductions.filter((d) =>
      d.created_at?.startsWith(this.filteredDate)
    );

    this.resetVisibleItems();
  }

  filterByMonth(): void {
    if (!this.filteredMonth) {
      this.clearFilters();
      return;
    }

    this.salaryMonth = this.filteredMonth;
    this.filteredDate = '';

    this.loans = this.originalLoans.filter(l =>
      l.created_at?.startsWith(this.filteredMonth)
    );

    this.bonuses = this.originalBonuses.filter(b =>
      b.created_at?.startsWith(this.filteredMonth)
    );

    this.deductions = this.originalDeductions.filter(d =>
      d.created_at?.startsWith(this.filteredMonth)
    );

    this.resetVisibleItems();
  }

  clearFilters(): void {
    this.filteredDate = '';
    this.filteredMonth = '';
    this.salaryMonth = this.currentMonth;

    this.loans = [...this.originalLoans];
    this.bonuses = [...this.originalBonuses];
    this.deductions = [...this.originalDeductions];

    this.resetVisibleItems();
  }

  resetVisibleItems(): void {
    this.loanLimit = 6;
    this.bonusLimit = 6;
    this.deductionLimit = 6;

    this.applyVisibleItems();
  }

  applyVisibleItems(): void {
    this.visibleLoans = this.loans.slice(0, this.loanLimit);
    this.visibleBonuses = this.bonuses.slice(0, this.bonusLimit);
    this.visibleDeductions = this.deductions.slice(0, this.deductionLimit);

    this.hasMoreData =
      this.visibleLoans.length < this.loans.length ||
      this.visibleBonuses.length < this.bonuses.length ||
      this.visibleDeductions.length < this.deductions.length;
  }

  loadMore(): void {
    if (!this.hasMoreData || this.isLoadingMore) return;

    this.isLoadingMore = true;

    this.loanLimit += this.loadStep;
    this.bonusLimit += this.loadStep;
    this.deductionLimit += this.loadStep;

    this.applyVisibleItems();

    setTimeout(() => {
      this.isLoadingMore = false;
    }, 250);
  }

  get totalLoans(): number {
    return this.loans.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }

  get totalBonuses(): number {
    return this.bonuses.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }

  get totalDeductions(): number {
    return this.deductions.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  }

  get netSalary(): number {
    const baseSalary = Number(this.salary || 0);
    return baseSalary + this.totalBonuses - this.totalLoans - this.totalDeductions;
  }

  get popupTitle(): string {
    if (this.finId === 1) {
      return this.isAr ? 'إضافة سلفة' : 'Add Loan';
    }

    if (this.finId === 2) {
      return this.isAr ? 'إضافة خصم' : 'Add Deduction';
    }

    return this.isAr ? 'إضافة بونص' : 'Add Bonus';
  }

  paySalary(): void {
    const month = this.salaryMonth;

    const currentBonuses = this.originalBonuses.filter(b =>
      b.created_at?.startsWith(month)
    );

    const currentLoans = this.originalLoans.filter(l =>
      l.created_at?.startsWith(month)
    );

    const currentDeductions = this.originalDeductions.filter(d =>
      d.created_at?.startsWith(month)
    );

    const totalBonuses = currentBonuses.reduce(
      (sum, b) => sum + Number(b.amount || 0), 0
    );

    const totalLoans = currentLoans.reduce(
      (sum, l) => sum + Number(l.amount || 0), 0
    );

    const totalDeductions = currentDeductions.reduce(
      (sum, d) => sum + Number(d.amount || 0), 0
    );

    const baseSalary = Number(this.salary || 0);
    const netSalary = baseSalary + totalBonuses - totalLoans - totalDeductions;

    Swal.fire({
      title: this.isAr ? 'متأكد؟' : 'Are You Sure?',
      html: this.isAr
        ? `
          الراتب الأساسي: ${baseSalary}<br>
          البونص: ${totalBonuses}<br>
          السلف: ${totalLoans}<br>
          الخصومات: ${totalDeductions}<br>
          <b>الصافي: ${netSalary}</b>
        `
        : `
          Base Salary: ${baseSalary}<br>
          Bonuses: ${totalBonuses}<br>
          Loans: ${totalLoans}<br>
          Deductions: ${totalDeductions}<br>
          <b>Net: ${netSalary}</b>
        `,
      showCancelButton: true,
      confirmButtonText: this.isAr ? 'اه' : 'YES',
      cancelButtonText: this.isAr ? 'لا' : 'NO'
    }).then((result) => {
      if (result.isConfirmed) {
        this.addEx(this.name, netSalary, month);
      }
    });
  }

  addLoan(): void {
    this.showFinPopUp = true;
    this.finId = 1;
    this.amount = '';
    this.note = '';
  }

  addDeduction(): void {
    this.showFinPopUp = true;
    this.finId = 2;
    this.amount = '';
    this.note = '';
  }

  addBonuses(): void {
    this.showFinPopUp = true;
    this.finId = 3;
    this.amount = '';
    this.note = '';
  }

  close(): void {
    this.finId = null;
    this.showFinPopUp = false;
    this.amount = '';
    this.note = '';
  }

  addEx(employeeName: any, total: any, month: any): void {
    const bonusPayload = {
      warehouse_id: localStorage.getItem('posId'),
      employee_id: this.userId,
      username: localStorage.getItem('username'),
      amount: total,
      note: month ? `${employeeName} - ${month}` : `${employeeName} - `,
      type: month ? '1' : '2'
    };

    this._FinancialManager.addexpenses(bonusPayload).subscribe({
      next: () => {
        Swal.fire({
          icon: 'success',
          title: this.isAr ? 'تمام' : 'success',
          text: this.isAr ? 'المرتب اتضاف للمصروفات' : 'Salary payed',
        });
      },
      error: (error) => console.error(error),
    });
  }

  onSubmit(): void {
    if (!this.amount || Number(this.amount) <= 0) {
      Swal.fire({
        icon: 'warning',
        title: this.isAr ? 'تنبيه' : 'Warning',
        text: this.isAr ? 'اكتب مبلغ صحيح' : 'Please enter a valid amount',
      });
      return;
    }

    this.contentReady = true;

    const bonusPayload = {
      warehouse_id: localStorage.getItem('posId'),
      amount: this.amount,
      note: this.note
    };

    if (this.finId === 1) {
      this._employessService.addLoan(this.id, bonusPayload).subscribe({
        next: () => {
          this.contentReady = false;
          this.getOneEmployee();
          this.close();
          this.addEx(this.name, this.amount, null);
        },
        error: (error) => {
          this.contentReady = false;
          console.error(error);
        },
      });

    } else if (this.finId === 2) {
      this._employessService.addDeduction(this.id, bonusPayload).subscribe({
        next: () => {
          this.contentReady = false;
          this.getOneEmployee();
          this.close();
        },
        error: (error) => {
          this.contentReady = false;
          console.error(error);
        },
      });

    } else {
      this._employessService.addBonus(this.id, bonusPayload).subscribe({
        next: () => {
          this.contentReady = false;
          this.getOneEmployee();
          this.close();
        },
        error: (error) => {
          this.contentReady = false;
          console.error(error);
        },
      });
    }
  }

  onAmountInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9.]/g, '');
    this.amount = input.value;
  }

  formatRecordDate(date: any): string {
    if (!date) return '-';
    return date.toString().substring(0, 16).replace('T', ' ');
  }
}